import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getFacilityUsers } from '../../services/userService';
import { getAppointmentsForTimeline } from '../../services/appointmentService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Paper,
  useTheme,
  ButtonGroup,
  Button,
  Grid,
  Avatar,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  ViewWeek as ViewWeekIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { UserRole, USER_ROLE_DISPLAY_NAMES } from '../../types/enums';
import { useLocale } from '../../context/LocaleContext';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { useAuth } from '../../context/AuthContext';

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '600px',
}));

const TimelineHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const TimelineGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '200px 1fr',
  flex: 1,
  overflow: 'hidden',
}));

const StaffColumn = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
  overflow: 'auto',
}));

const HoursGrid = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'auto',
  backgroundColor: 'white',
}));

const HourColumn = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  width: '80px',
  minHeight: '100%',
  position: 'relative',
}));

const StaffRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  padding: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '60px',
}));

const TimeSlotGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(24, 80px)',
  gridTemplateRows: 'auto',
  minHeight: '100%',
}));

const AssignmentBlock = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'overlap'
})(({ theme, overlap, color }) => ({
  position: 'absolute',
  backgroundColor: color || theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(0.5, 1),
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
  zIndex: overlap ? 5 : 1,
  border: overlap ? `2px solid ${theme.palette.warning.main}` : 'none',
  opacity: overlap ? 0.9 : 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:hover': {
    opacity: 0.8,
    zIndex: 10,
  },
}));

const StaffTimelineView = ({ selectedFacility, onAppointmentClick }) => {
  const { t } = useLocale();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useAuth(); // Use AuthContext instead of Redux for user data
  const { userFacilities } = useSelector((state) => state.facilities);

  // selectedFacility should be the facility ID string
  const facilityId = typeof selectedFacility === 'object' ? selectedFacility?.id : selectedFacility;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [staffList, setStaffList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilityUsers, setFacilityUsers] = useState([]);
  const hoursGridRef = useRef(null);

  // Generate color palette for staff assignments
  const colorPalette = useMemo(() => [
    '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828',
    '#00796b', '#303f9f', '#689f38', '#f9a825', '#8e24aa',
    '#d32f2f', '#0097a7', '#512da8', '#afb42b', '#ff8f00'
  ], []);

  const getStaffColor = useCallback((staffId) => {
    const index = staffList.findIndex(staff => staff.id === staffId);
    return colorPalette[index % colorPalette.length];
  }, [staffList, colorPalette]);

  // Cache service handles facility data automatically

  const fetchFacilityStaff = useCallback(async () => {
    if (!facilityId || facilityId === '' || !user?.uid) {
      console.log('StaffTimelineView: Missing facilityId or user', { facilityId, uid: user?.uid });
      return;
    }
    
    try {
      setLoading(true);
      console.log('StaffTimelineView: Fetching staff for facility:', facilityId);
      
      // Fetch users assigned to this facility via user_facilities collection
      const fetchedUsers = await getFacilityUsers(facilityId);
      console.log('StaffTimelineView: Fetched users:', fetchedUsers?.length);
      
      if (!fetchedUsers || fetchedUsers.length === 0) {
        console.log('StaffTimelineView: No users found for facility');
        setStaffList([]);
        setFacilityUsers([]);
        return;
      }
      
      // Filter users for healthcare staff roles (case-insensitive check)
      const healthcareRoles = [
        UserRole.DOCTOR,
        UserRole.ATTENDING_PHYSICIAN,
        UserRole.NURSE,
        UserRole.PHARMACIST,
        UserRole.CASE_MANAGER,
        UserRole.SOCIAL_WORKER,
        UserRole.PHYSICAL_THERAPIST,
        UserRole.OCCUPATIONAL_THERAPIST,
        UserRole.SPEECH_LANGUAGE_PATHOLOGIST,
        UserRole.DIETITIAN
      ];

      const filteredStaff = fetchedUsers.filter(u => {
        const roles = u.roles || [u.role].filter(Boolean);
        // Case-insensitive role matching
        const hasHealthcareRole = roles.some(role => 
          healthcareRoles.some(hr => hr.toLowerCase() === (role || '').toLowerCase())
        );
        return hasHealthcareRole;
      });

      // Remove duplicates by ID
      const uniqueStaff = filteredStaff.filter((staff, index, self) => 
        index === self.findIndex(s => s.id === staff.id)
      );
      
      console.log('StaffTimelineView: Healthcare staff count:', uniqueStaff.length);
      
      setStaffList(uniqueStaff);
      setFacilityUsers(uniqueStaff);
    } catch (error) {
      console.error('StaffTimelineView: Error fetching facility staff:', error);
      setStaffList([]);
      setFacilityUsers([]);
    } finally {
      setLoading(false);
    }
  }, [facilityId, user?.uid]);

  // Add missing useEffect to fetch facility staff
  useEffect(() => {
    if (facilityId && facilityId !== '' && user?.uid) {
      fetchFacilityStaff();
    } else {
      // Clear staff list when no facility is selected
      setStaffList([]);
      setFacilityUsers([]);
      setLoading(false);
    }
  }, [facilityId, user?.uid, fetchFacilityStaff]);

  const fetchAppointments = useCallback(async () => {
    if (!facilityId || facilityId === '') {
      setAppointments([]);
      return;
    }
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentData = await getAppointmentsForTimeline(facilityId, startOfDay, endOfDay);
      
      // Convert date strings back to Date objects if needed
      const processedAppointments = appointmentData.map(appointment => ({
        ...appointment,
        date: appointment.date instanceof Date ? appointment.date : new Date(appointment.date)
      }));
      
      setAppointments(processedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  }, [facilityId, currentDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const navigateDate = useCallback((direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    
    // Limit navigation to 12 months in either direction
    const today = new Date();
    const maxPastDate = new Date(today);
    maxPastDate.setMonth(maxPastDate.getMonth() - 12);
    const maxFutureDate = new Date(today);
    maxFutureDate.setMonth(maxFutureDate.getMonth() + 12);
    
    if (newDate >= maxPastDate && newDate <= maxFutureDate) {
      setCurrentDate(newDate);
    }
  }, [currentDate]);

  const navigateWeek = useCallback((direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    
    // Same 12-month limit check
    const today = new Date();
    const maxPastDate = new Date(today);
    maxPastDate.setMonth(maxPastDate.getMonth() - 12);
    const maxFutureDate = new Date(today);
    maxFutureDate.setMonth(maxFutureDate.getMonth() + 12);
    
    if (newDate >= maxPastDate && newDate <= maxFutureDate) {
      setCurrentDate(newDate);
    }
  }, [currentDate]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Generate hour labels (next 24 hours from now)
  const hours = useMemo(() => {
    const startHour = currentDate.getHours();
    const hourLabels = [];
    for (let i = 0; i < 24; i++) {
      const hour = (startHour + i) % 24;
      const dayOffset = Math.floor((startHour + i) / 24);
      // Show day label if crossing midnight
      let label = `${hour.toString().padStart(2, '0')}:00`;
      if (dayOffset > 0) label += ` (+${dayOffset}d)`;
      hourLabels.push(label);
    }
    return hourLabels;
  }, [currentDate]);

  // Map appointments to the new hour slots
  const processedAppointments = useMemo(() => {
    const staffAppointments = {};
    const startHour = currentDate.getHours();
    const startTime = new Date(currentDate);
    startTime.setMinutes(0, 0, 0);
    staffList.forEach(staff => {
      staffAppointments[staff.id] = [];
    });
    appointments.forEach(appointment => {
      // Find staff member by doctorName or doctorId
      const staff = staffList.find(s => 
        s.id === appointment.doctorId || 
        s.name === appointment.doctorName ||
        s.email === appointment.doctorEmail
      );
      if (staff) {
        // Calculate the position relative to the new start time
        const diffMs = appointment.date - startTime;
        const startPosition = diffMs / (1000 * 60 * 60); // hours from startTime
        const duration = appointment.duration || 60; // Default 1 hour
        const width = duration / 60;
        // Only show appointments that are within the next 24 hours
        if (startPosition + width > 0 && startPosition < 24) {
          const appointmentData = {
            ...appointment,
            staff: staff,
            startHour: appointment.date.getHours(),
            startMinutes: appointment.date.getMinutes(),
            duration,
            startPosition,
            width,
            color: getStaffColor(staff.id)
          };
          staffAppointments[staff.id].push(appointmentData);
        }
      }
    });
    // Detect overlapping appointments
    Object.keys(staffAppointments).forEach(staffId => {
      const staffAppts = staffAppointments[staffId];
      staffAppts.forEach((appt, index) => {
        const overlapping = staffAppts.filter((other, otherIndex) => {
          if (index === otherIndex) return false;
          const apptEnd = appt.startPosition + appt.width;
          const otherEnd = other.startPosition + other.width;
          return (appt.startPosition < otherEnd && apptEnd > other.startPosition);
        });
        appt.hasOverlap = overlapping.length > 0;
        appt.overlapIndex = overlapping.length > 0 ? index % 3 : 0; // Stack overlapping appointments
      });
    });
    
    return staffAppointments;
  }, [appointments, staffList, getStaffColor, currentDate]);

  const formatDate = useCallback((date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const getPrimaryRole = useCallback((staff) => {
    const roles = staff.roles || [staff.role].filter(Boolean);
    return roles[0] || 'user';
  }, []);

  const isToday = useCallback((date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const isWeekend = useCallback((date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  }, []);

  const getCurrentTimePosition = useCallback(() => {
    if (!isToday(currentDate)) return null;
    const now = new Date();
    return now.getHours() + (now.getMinutes() / 60);
  }, [currentDate, isToday]);

  // Auto-scroll to current hour if today
  useEffect(() => {
    if (!isToday(currentDate) || !hoursGridRef.current) return;
    
    const scrollToCurrentHour = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const columnWidth = 80; // px
      const grid = hoursGridRef.current;
      if (!grid) return;
      
      // Calculate max scrollLeft
      const maxScrollLeft = grid.scrollWidth - grid.clientWidth;
      // Try to center the current hour
      const targetScroll = Math.min(Math.max(0, currentHour * columnWidth - grid.clientWidth / 2 + columnWidth / 2), maxScrollLeft);
      
      // Use double requestAnimationFrame to ensure grid is rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (grid) {
            grid.scrollLeft = targetScroll;
          }
        });
      });
    };

    // Small delay to ensure the component is fully rendered
    const timeoutId = setTimeout(scrollToCurrentHour, 100);
    return () => clearTimeout(timeoutId);
  }, [currentDate, isToday]);

  // Listen for facility changes and refresh timeline data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('StaffTimelineView: Facility changed, refreshing timeline data');
    // Refresh staff list and appointments when facility changes
    fetchFacilityStaff();
    fetchAppointments();
  };

  useFacilityChangeListener(handleFacilityChange);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show message if no staff is available
  if (!staffList || staffList.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {!facilityId || facilityId === '' ? 'Select a Facility' : 'No Staff Available'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!facilityId || facilityId === '' ? 
                'Please select a facility from the form to view the staff timeline.' : 
                'No healthcare staff found for the selected facility.'
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TimelineHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Staff Timeline
          </Typography>
          <Chip 
            label={formatDate(currentDate)}
            color={isToday(currentDate) ? "success" : isWeekend(currentDate) ? "warning" : "primary"}
            size="small"
            sx={{ 
              fontWeight: 600,
              backgroundColor: isToday(currentDate) ? 'success.main' : 
                             isWeekend(currentDate) ? 'warning.main' : 'primary.main'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Previous Week" arrow>
            <IconButton onClick={() => navigateWeek(-1)} size="small" sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChevronLeftIcon fontSize="small" />
                <ChevronLeftIcon fontSize="small" sx={{ ml: -1 }} />
              </Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="Previous Day" arrow>
            <IconButton onClick={() => navigateDate(-1)} size="small" sx={{ color: 'white' }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={goToToday}
            startIcon={<TodayIcon fontSize="small" />}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
            }}
          >
            Today
          </Button>
          <Tooltip title="Next Day" arrow>
            <IconButton onClick={() => navigateDate(1)} size="small" sx={{ color: 'white' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Next Week" arrow>
            <IconButton onClick={() => navigateWeek(1)} size="small" sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChevronRightIcon fontSize="small" />
                <ChevronRightIcon fontSize="small" sx={{ ml: -1 }} />
              </Box>
            </IconButton>
          </Tooltip>
        </Box>
      </TimelineHeader>

      <CardContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
        {/* Header Row: Filler cell + Hour labels */}
        <Box sx={{ display: 'flex', width: '100%' }}>
          {/* Filler cell for staff column */}
          <Box sx={{
            width: '200px',
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.50',
            fontSize: '0.75rem',
            fontWeight: 500,
            flexShrink: 0,
          }} />
          {/* Hour labels */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(24, 80px)',
            flex: 1,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}>
            {hours.map((hour, index) => (
              <Box
                key={`hour-${index}-${hour}`}
                sx={{
                  p: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              >
                {hour}
              </Box>
            ))}
          </Box>
        </Box>
        <TimelineGrid>
          {/* Staff Column */}
          <StaffColumn>
            {staffList.map((staff) => (
              <StaffRow key={staff.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 1.5 }}>
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      backgroundColor: getStaffColor(staff.id),
                      fontSize: '0.75rem'
                    }}
                  >
                    {staff.name ? staff.name.charAt(0).toUpperCase() : staff.email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                      {staff.name || staff.email}
                    </Typography>
                  </Box>
                </Box>
              </StaffRow>
            ))}
          </StaffColumn>

          {/* Timeline Grid */}
          <Box ref={hoursGridRef} sx={{ width: '100%', maxWidth: '1200px', overflowX: 'auto', overflowY: 'hidden' }}>
            <HoursGrid sx={{ minWidth: '1920px' }}>
              {/* Staff Timeline Rows */}
              {staffList.map((staff, staffIndex) => (
                <Box
                  key={staff.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(24, 80px)',
                    minHeight: '60px',
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: 'relative',
                    borderTop: staffIndex === 0 ? 1 : 0,
                    borderTopColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'grey.50'
                    }
                  }}
                >
                  {/* Hour Cells */}
                  {hours.map((hour, hourIndex) => (
                    <Box
                      key={`${staff.id}-hour-${hourIndex}`}
                      sx={{
                        borderRight: hourIndex === 23 ? 0 : 1,
                        borderColor: 'divider',
                        borderTop: 0,
                        borderBottom: 0,
                        position: 'relative',
                        backgroundColor: hourIndex % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'
                      }}
                    />
                  ))}

                  {/* Current Time Indicator */}
                  {getCurrentTimePosition() !== null && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${getCurrentTimePosition() * 80}px`,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: 'error.main',
                        zIndex: 100,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-4px',
                          left: '-4px',
                          width: '10px',
                          height: '10px',
                          backgroundColor: 'error.main',
                          borderRadius: '50%',
                        }
                      }}
                    />
                  )}

                  {/* Appointment Blocks */}
                  {processedAppointments[staff.id]?.map((appointment, index) => {
                    return (
                      <AssignmentBlock
                        key={`${staff.id}-appointment-${appointment.id}-${index}`}
                        overlap={appointment.hasOverlap}
                        color={appointment.color}
                        onClick={() => onAppointmentClick && onAppointmentClick(appointment)}
                        sx={{
                          left: `${appointment.startPosition * 80}px`,
                          width: `${appointment.width * 80 - 4}px`,
                          top: `${4 + (appointment.overlapIndex * 16)}px`,
                          height: '20px',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          }
                        }}
                      >
                        <Tooltip title={`${appointment.patientName} - ${appointment.purpose}`} arrow>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {appointment.patientName}
                          </Box>
                        </Tooltip>
                      </AssignmentBlock>
                    );
                  })}
                </Box>
              ))}
            </HoursGrid>
          </Box>
        </TimelineGrid>
      </CardContent>
    </Card>
  );
};

export default React.memo(StaffTimelineView); 